import isEmpty from 'lodash/fp/isEmpty';

import {
    MANUAL_VEHICLE_ATTRIBUTES,
    VEHICLE_ATTRIBUTES_UNITS,
    VehicleAttributeNames,
    type VehicleAttributes,
} from '../../application/redux/options/optionsReducer';
import { TollVehicleType } from '../options/vehicle/VehicleAttributePresets';
import type { WaypointWithCoordinates } from './fetchWaypointCoordinates';
import { mapEmissionTypeValue, mapOption, mapVehicleTypeValue } from './mappers/mapOptions';

const pathOfHereApi = '/v8/routes';

export interface RouteParameters {
    departureTime: string;
    departureDate: string;
    waypoints: WaypointWithCoordinates[];
    vehicleAttributes: VehicleAttributes;
    currency: string;
    considerTraffic: boolean;
    avoidMotorway?: boolean;
    avoidToll?: boolean;
    calculateAlternatives: boolean;
}

const createModeOptionsUrl = (parameters: RouteParameters) => {
    let parameterString = '';
    parameterString += '&routingMode=fast';
    parameterString += `&transportMode=${
        parameters.vehicleAttributes[VehicleAttributeNames.TOLL_VEHICLE_TYPE] === TollVehicleType.CAR ? 'car' : 'truck'
    }`;
    return parameterString;
};

const createAvoidFeaturesUrl = (parameters: RouteParameters) => {
    let parameterString = '';
    parameterString += parameters.avoidMotorway ? ',controlledAccessHighway' : '';
    parameterString += parameters.avoidToll ? ',tollRoad' : '';
    return parameterString.length > 0 ? `&avoid[features]=${parameterString.slice(1)}` : '';
};

const createWaypointsParameterUrl = (waypoints: WaypointWithCoordinates[]) => {
    return waypoints.reduce((previous: string, current: WaypointWithCoordinates, index: number) => {
        if (index === 0) {
            return previous + `&origin=${current.coordinates.lat},${current.coordinates.lng}`;
        }
        if (index === waypoints.length - 1) {
            return previous + `&destination=${current.coordinates.lat},${current.coordinates.lng}`;
        }
        // multiple via parameters here are intentional, required by API
        return previous + `&via=${current.coordinates.lat},${current.coordinates.lng}`;
    }, '');
};

const createVehicleAttributesUrl = (vehicleAttributes: VehicleAttributes) => {
    let parameterString = '';
    for (const key in vehicleAttributes) {
        if (Object.hasOwn(vehicleAttributes, key) && !MANUAL_VEHICLE_ATTRIBUTES.includes(key)) {
            const unit = !isEmpty(VEHICLE_ATTRIBUTES_UNITS[key]) ? VEHICLE_ATTRIBUTES_UNITS[key] : '';
            parameterString += `&${mapOption(key)}=${convertUnits(vehicleAttributes[key], unit)}`;
        }
    }

    if (!isEmpty(vehicleAttributes)) {
        if (vehicleAttributes[VehicleAttributeNames.VEHICLE_NUMBER_AXLES]) {
            const totalAxles =
                Number(vehicleAttributes[VehicleAttributeNames.VEHICLE_NUMBER_AXLES] ?? null) +
                Number(vehicleAttributes[VehicleAttributeNames.TRAILER_NUMBER_AXLES] ?? null);
            parameterString += `&${mapOption(VehicleAttributeNames.VEHICLE_NUMBER_AXLES)}=${totalAxles}`;
        }

        const isTrailer = Number(vehicleAttributes[VehicleAttributeNames.TRAILER_NUMBER_AXLES] ?? null) > 0;
        if (isTrailer) {
            parameterString += '&vehicle[trailerCount]=1';
            parameterString += `&vehicle[trailerAxleCount]=${vehicleAttributes[VehicleAttributeNames.TRAILER_NUMBER_AXLES]}`;
        }
        if (vehicleAttributes[VehicleAttributeNames.TOLL_VEHICLE_TYPE]) {
            const value = mapVehicleTypeValue(vehicleAttributes[VehicleAttributeNames.TOLL_VEHICLE_TYPE], isTrailer);
            if (value) {
                const key = mapOption(VehicleAttributeNames.TOLL_VEHICLE_TYPE);
                parameterString += `&${key}=${value}`;
            }
        }
        if (vehicleAttributes[VehicleAttributeNames.EMISSION_TYPE]) {
            const emissionTypeValue = mapEmissionTypeValue(vehicleAttributes[VehicleAttributeNames.EMISSION_TYPE]);
            if (emissionTypeValue) {
                const key = mapOption(VehicleAttributeNames.EMISSION_TYPE);
                parameterString += `&${key}=${emissionTypeValue}`;
            }
        }
    }

    return parameterString;
};

const convertUnits = (value: string | number | undefined, unit: string): string | number | undefined => {
    if (unit === 'm') {
        return Number(Number(value) * 100).toFixed(0);
    }
    if (unit === 't') {
        return Number(Number(value) * 1000).toFixed(0);
    }
    // No conversion needed
    return value;
};

const createVehicleCostsParameterUrl = (currency: string): string => `&currency=${currency}`;

const createDepartureTimeParameterUrl = (parameters: RouteParameters): string => {
    if (parameters.considerTraffic) {
        return `&departureTime=${`${parameters.departureDate}T${parameters.departureTime}:00`.replace('+', '%2B')}`;
    }
    return '&departureTime=any';
};

export const createRouteQuery = (parameters: RouteParameters, apiKey: string) => {
    const departureTimeUrl = createDepartureTimeParameterUrl(parameters);
    const modeOptionsUrl = createModeOptionsUrl(parameters);
    const avoidFeaturesUrl = createAvoidFeaturesUrl(parameters);
    const waypointParameterUrl = createWaypointsParameterUrl(parameters.waypoints);
    const vehicleAttributesUrl = createVehicleAttributesUrl(parameters.vehicleAttributes);
    const vehicleCostParametersUrl = createVehicleCostsParameterUrl(parameters.currency);
    const numberOfAlternativesUrl =
        parameters.calculateAlternatives && parameters.waypoints && Object.keys(parameters.waypoints).length === 2
            ? '&alternatives=2'
            : '';
    const apiKeyParameter = `&apiKey=${apiKey}`;

    let additionalParameters = '?return=polyline,summary,tolls&tolls[summaries]=total';
    additionalParameters += departureTimeUrl;
    additionalParameters += modeOptionsUrl;
    additionalParameters += avoidFeaturesUrl;
    additionalParameters += waypointParameterUrl;
    additionalParameters += vehicleAttributesUrl;
    additionalParameters += numberOfAlternativesUrl;
    additionalParameters += vehicleCostParametersUrl;
    additionalParameters += apiKeyParameter;

    return pathOfHereApi + decodeURIComponent(additionalParameters);
};
